/**
 * DOM Actions Generator
 *
 * Converts DOM events captured by the spy into Playwright test actions.
 * Handles special cases like Google Places, Radix UI, Stripe, etc.
 */

import type { DOMEvent } from "../../lib/dom-event-spy";
import type { TraceEvent } from "../trace-inspector";
import { escapeString } from "./utils";

// ============================================================================
// PII Field Detection
// ============================================================================

/**
 * Check if a DOM event is for a consumer PII field (firstName, lastName, DOB, email)
 * Returns the field type if it's a PII field, null otherwise
 */
export function getConsumerPIIFieldType(
  domEvent: DOMEvent,
): "firstName" | "lastName" | "dateOfBirth" | "email" | null {
  const label = domEvent.elementInfo.label?.toLowerCase() || "";
  const placeholder = domEvent.elementInfo.placeholder?.toLowerCase() || "";
  const ariaLabel = domEvent.selector.toLowerCase();

  // First Name detection
  if (
    label.includes("first name") ||
    placeholder.includes("first name") ||
    ariaLabel.includes('name: "first name"')
  ) {
    return "firstName";
  }

  // Last Name detection
  if (
    label.includes("last name") ||
    placeholder.includes("last name") ||
    ariaLabel.includes('name: "last name"')
  ) {
    return "lastName";
  }

  // Date of Birth detection
  if (
    label.includes("date of birth") ||
    label.includes("dob") ||
    placeholder.includes("date of birth") ||
    placeholder.includes("mm/dd/yyyy") ||
    ariaLabel.includes('name: "date of birth"')
  ) {
    return "dateOfBirth";
  }

  // Email detection (including combined phone/email fields)
  if (
    label.includes("email") ||
    placeholder.includes("email") ||
    ariaLabel.includes('name: "email"') ||
    ariaLabel.includes('name: "phone number or email"')
  ) {
    return "email";
  }

  return null;
}

/**
 * Check if a DOM event is for an insurance member ID field
 */
export function isInsuranceMemberIdField(domEvent: DOMEvent): boolean {
  const label = domEvent.elementInfo.label?.toLowerCase() || "";
  const placeholder = domEvent.elementInfo.placeholder?.toLowerCase() || "";
  const ariaLabel = domEvent.selector.toLowerCase();

  return (
    label.includes("member id") ||
    label.includes("memberid") ||
    label.includes("subscriber id") ||
    placeholder.includes("member id") ||
    placeholder.includes("memberid") ||
    ariaLabel.includes('name: "member id"')
  );
}

// ============================================================================
// DOM Action Generation
// ============================================================================

/**
 * Generate a single Playwright action from a DOM event
 */
export function generateDOMAction(domEvent: DOMEvent): string | null {
  const selector = domEvent.selector;

  // The selector is already in Playwright format (e.g., getByRole("button", { name: "Next" }))
  // We just need to add the page prefix and the action

  switch (domEvent.type) {
    case "click":
      return generateClickAction(domEvent, selector);

    case "input":
      return generateInputAction(domEvent, selector);

    case "change":
      return generateChangeAction(domEvent, selector);

    case "select":
      // For Radix UI Select components (role="option"), use click
      // The selector is already getByRole("option", { name: "..." })
      return `await page.${selector}.click();`;

    default:
      return `// Unknown DOM event type: ${domEvent.type}`;
  }
}

/**
 * Generate click action with special handling for various components
 */
function generateClickAction(domEvent: DOMEvent, selector: string): string {
  // Special handling for new Google Places PlaceAutocompleteElement
  if (
    selector.includes("gmp-place-autocomplete") &&
    selector.includes("getByRole")
  ) {
    // Wait for dropdown options to appear, click first suggestion, then wait for auto-fill
    return `await page.locator("gmp-place-autocomplete").getByRole("option").first().waitFor({ state: "visible" });\n  await page.locator("gmp-place-autocomplete").getByRole("option").first().click();\n  await page.waitForTimeout(1000); // Wait for address auto-fill from API`;
  }

  // Special handling for old Google Places autocomplete (.pac-item)
  if (selector.includes(".pac-item")) {
    // Wait for dropdown, click first suggestion, then wait for auto-fill to complete
    // (Google Places triggers place_changed which calls setValue for city/state/zip)
    // Using 1000ms wait because the API call + form update can take time
    return `await page.locator(".pac-container").waitFor({ state: "visible" });\n  await page.locator(".pac-item").first().click();\n  await page.waitForTimeout(1000); // Wait for address auto-fill from API`;
  }

  // Special handling for pharmacy search results
  if (selector.includes("[data-pharmacy-result]")) {
    // Wait for pharmacy search API results, then click first result
    return `await page.locator("[data-pharmacy-result]").first().waitFor({ state: "visible" });\n  await page.locator("[data-pharmacy-result]").first().click();`;
  }

  // Special handling for signature canvas - simulate a simple signature stroke
  if (selector.includes("[data-signature-canvas]")) {
    return `// Draw a simple signature stroke on the canvas
  const canvas = page.locator("[data-signature-canvas] canvas");
  const box = await canvas.boundingBox();
  if (box) {
    // Draw a simple cursive-like signature stroke
    await page.mouse.move(box.x + box.width * 0.1, box.y + box.height * 0.5);
    await page.mouse.down();
    await page.mouse.move(box.x + box.width * 0.3, box.y + box.height * 0.3, { steps: 10 });
    await page.mouse.move(box.x + box.width * 0.5, box.y + box.height * 0.6, { steps: 10 });
    await page.mouse.move(box.x + box.width * 0.7, box.y + box.height * 0.4, { steps: 10 });
    await page.mouse.move(box.x + box.width * 0.9, box.y + box.height * 0.5, { steps: 10 });
    await page.mouse.up();
  }`;
  }

  // Special handling for Radix Select combobox triggers
  // Radix uses pointerdown events and portals, so we need to ensure the click
  // actually triggers the dropdown. Using dispatchEvent with a real PointerEvent
  // is more reliable than Playwright's click() for these components.
  if (domEvent.elementInfo.role === "combobox") {
    return `// Radix Select - dispatch pointerdown event to trigger dropdown
  await page.${selector}.dispatchEvent("pointerdown", { button: 0, pointerType: "mouse" });
  await page.locator("[data-radix-select-viewport]").waitFor({ state: "visible" });`;
  }

  return `await page.${selector}.click();`;
}

/**
 * Generate input action with special handling for various input types
 */
function generateInputAction(
  domEvent: DOMEvent,
  selector: string,
): string | null {
  // For radios/checkboxes, use click instead of fill
  if (
    domEvent.elementInfo.type === "checkbox" ||
    domEvent.elementInfo.type === "radio"
  ) {
    return `await page.${selector}.click();`;
  }

  // Skip empty fills - they're noise from initial focus events
  const inputValue = String(domEvent.value || "");
  if (!inputValue) {
    return null;
  }

  // ========================================================================
  // Isomorphic Property Testing: Replace PII with consumer variables
  // ========================================================================

  // Check if this is a consumer PII field (firstName, lastName, DOB)
  const piiFieldType = getConsumerPIIFieldType(domEvent);
  if (piiFieldType === "firstName") {
    return `await page.${selector}.fill(consumer.firstName);`;
  }
  if (piiFieldType === "lastName") {
    return `await page.${selector}.fill(consumer.lastName);`;
  }
  if (piiFieldType === "dateOfBirth") {
    return `await page.${selector}.fill(format(consumer.dateOfBirth, "MM/dd/yyyy"));`;
  }
  if (piiFieldType === "email") {
    return `await page.${selector}.fill(consumer.email);`;
  }

  // Check if this is an insurance member ID field - use a placeholder value
  // NOTE: Fixture ID is now embedded in lastName (not memberId) because
  // getInsuranceStatus overwrites insurance data. See generateExistingUserSetup.
  if (isInsuranceMemberIdField(domEvent)) {
    return `await page.${selector}.fill("TEST-MEMBER-ID");`;
  }

  // ========================================================================

  // Special handling for OTP inputs (input-otp library)
  // The library uses an input with data-input-otp="true" attribute
  // Detect by: selector pattern, or 6-digit numeric value (likely OTP)
  const isOTPInput =
    selector.includes("input-otp") ||
    selector.includes("[data-input-otp") ||
    (inputValue.length === 6 && /^\d+$/.test(inputValue)); // 6-digit numeric = likely OTP
  if (isOTPInput) {
    return `// OTP Input: Fill the hidden input used by input-otp library
  await page.locator('input[data-input-otp="true"]').fill("${escapeString(
    inputValue,
  )}");`;
  }

  // Use pressSequentially for Google Autocomplete fields (needs real key events to trigger dropdown)
  // Also add a wait before to ensure the Google Places API has loaded
  // Detect by label OR by the gmp-place-autocomplete selector pattern
  const isGooglePlacesInput =
    domEvent.elementInfo.label === "Street address" ||
    selector.includes("gmp-place-autocomplete");
  if (isGooglePlacesInput) {
    // Must click to focus the input first, then type - the autocomplete won't activate without focus
    return `await page.waitForTimeout(1000); // Wait for Google Places API to initialize
  await page.${selector}.click(); // Focus the input to activate autocomplete
  await page.${selector}.pressSequentially("${escapeString(
    inputValue,
  )}", { delay: 100 });`;
  }

  return `await page.${selector}.fill("${escapeString(inputValue)}");`;
}

/**
 * Generate change action for selects and checkboxes/radios
 */
function generateChangeAction(domEvent: DOMEvent, selector: string): string {
  const changeValue = escapeString(String(domEvent.value || ""));

  // Check if it looks like a select based on element info
  if (domEvent.elementInfo.tagName === "select") {
    return `await page.${selector}.selectOption("${changeValue}");`;
  }

  // For radios/checkboxes, just click
  if (
    domEvent.elementInfo.type === "checkbox" ||
    domEvent.elementInfo.type === "radio"
  ) {
    return `await page.${selector}.click();`;
  }

  return `await page.${selector}.fill("${changeValue}");`;
}

// ============================================================================
// Test Actions Generation
// ============================================================================

/**
 * Generate test actions from machine events with DOM events
 * @param events - Trace events to process
 * @param isExistingUser - If true, skip waitForFunction for createUserAndConsumer and createInsurance states
 *                         since these are pre-created via STU builders for existing users
 */
export function generateTestActions(
  events: TraceEvent[],
  isExistingUser: boolean,
): string {
  const lines: string[] = [];
  let currentSection = "";

  // States to skip for existing users - user/consumer/insurance are pre-created via STU
  const existingUserSkipStates = ["createUserAndConsumer", "createInsurance"];

  for (const event of events) {
    const eventType = event.machineEvent?.type || "UNKNOWN";

    // Add section comment for state transitions
    if (event.fromState && event.fromState !== currentSection) {
      currentSection = event.fromState;
      lines.push("");
      lines.push(
        "  // ============================================================================",
      );
      lines.push(`  // State: ${currentSection}`);
      if (event.toState && event.toState !== currentSection) {
        lines.push(`  // Event: ${eventType} → ${event.toState}`);
      }
      lines.push(
        "  // ============================================================================",
      );
    }

    const domEvents = event.uiHint?.domEvents || [];

    if (domEvents.length === 0) {
      // No DOM events - this is an async/loading state (e.g., createUserAndConsumer, loadingPhotos)
      // The machine auto-transitions when the async operation completes
      if (event.toState && event.toState !== event.fromState) {
        // Skip user/consumer/insurance creation states for existing users
        // These are pre-created in beforeEach via STU builders
        if (
          isExistingUser &&
          event.fromState &&
          existingUserSkipStates.includes(event.fromState)
        ) {
          lines.push("");
          lines.push(
            `  // Skipped: ${event.fromState} (existing user - pre-created via STU)`,
          );
          continue;
        }

        // Special handling for Stripe Element payment state - fill card details in iframe
        // The DOM spy can't capture interactions inside Stripe's iframe
        const isStripeElementPaymentState =
          event.fromState === "stripeElementPayment";

        if (isStripeElementPaymentState) {
          lines.push("");
          lines.push(
            "  // Stripe Element Payment: Fill card details in Stripe iframe",
          );
          lines.push(
            '  await page.locator("div.StripeElement").waitFor({ state: "visible" });',
          );
          lines.push(
            "  const stripeFrame = page.frameLocator(\"//iframe[@title='Secure payment input frame']\");",
          );
          lines.push(
            '  await stripeFrame.getByPlaceholder("1234 1234 1234 1234").fill("4242 4242 4242 4242");',
          );
          lines.push(
            '  await stripeFrame.getByPlaceholder("MM / YY").fill("12/29");',
          );
          lines.push(
            '  await stripeFrame.getByPlaceholder("CVC").fill("123");',
          );
          lines.push(
            '  await stripeFrame.getByPlaceholder("12345").fill("12345");',
          );
          lines.push(
            '  await page.getByRole("button", { name: "Pay now" }).click();',
          );
          lines.push("");
        }

        // Special handling for POS payment processing state - simulate card tap
        // The state is "posPayment" (parent) with nested "processingPayment"
        // When in processingPayment, the reader is waiting for a card tap
        const isPOSPaymentState = event.fromState === "posPayment";

        if (isPOSPaymentState) {
          lines.push("");
          lines.push(
            "  // POS Payment: Simulate card tap via Stripe test helpers",
          );
          lines.push(
            "  // IMPORTANT: SIMULATED_READER_ID must be set to a simulated reader (not a real physical reader)",
          );
          lines.push("  // Wait for reader to be ready for card presentation");
          lines.push("  await page.waitForTimeout(2000);");
          lines.push("  if (SIMULATED_READER_ID) {");
          lines.push(
            "    await page.request.post('/api/stripe/simulate-card-tap', {",
          );
          lines.push(
            "      data: { readerId: SIMULATED_READER_ID, cardNumber: '4242424242424242' }",
          );
          lines.push("    });");
          lines.push("  } else {");
          lines.push(
            "    console.warn('STRIPE_SIMULATED_READER_ID not set - skipping POS payment simulation');",
          );
          lines.push("  }");
          lines.push("");
        }

        lines.push("");
        lines.push(`  // Async state: ${event.fromState} (loading/API call)`);
        lines.push(`  // Wait for transition to: ${event.toState}`);
        lines.push("  await page.waitForFunction(");
        lines.push("    () => {");
        lines.push(
          "      const stateId = new URL(window.location.href).searchParams.get('stateId');",
        );
        lines.push(
          "      const entry = JSON.parse(localStorage.getItem(`kiosk-state:${stateId}`) || '{}');",
        );
        lines.push(`      return entry.state?.value === "${event.toState}";`);
        lines.push("    },");
        lines.push("    { timeout: 30000 }");
        lines.push("  );");
        lines.push("");
      }
      continue;
    }

    // Add delay before insurance form submission to ensure form validation is ready
    if (currentSection === "insuranceInput.inputForm") {
      lines.push(
        "  await page.waitForTimeout(1000); // Wait for insurance form validation",
      );
    }

    // Generate Playwright actions from DOM events
    for (const domEvent of domEvents) {
      const action = generateDOMAction(domEvent);
      if (action) {
        lines.push(`  ${action}`);
      }
    }
  }

  return lines.join("\n");
}
